/**
 * Eva Company Detail (SF-3734).
 *
 * Drill-down for a single company's Eva usage, reached from the platform
 * overview's top-companies table (SF-3733). Shows four headline tiles
 * (this-month spend, last-month spend, % of the company's monthly cap used,
 * average active-user spend), a 12-month monthly trend chart and a 30-day daily
 * trend chart, a top-5 users table, and a paginated full user list with
 * per-user spend, message count, and % of the per-user $10 cap.
 *
 * Data comes from sf-api `/admin/eva/usage/company/{companyId}*` (SF-3731) via
 * the shared Eva API client. Every field is null-guarded and money strings are
 * parsed only at display time. Access is gated by `admin:eva:usage:read` via
 * the access-control provider; unauthorized users see a 403 and no request is
 * made. Reuses the SF-3733 shared KpiTile / TrendChart / DataTable components.
 */
import { useEffect, useMemo, useState } from 'react';
import { useCan } from '@refinedev/core';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Alert,
  Button,
  Col,
  Input,
  Result,
  Row,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  DollarOutlined,
  InfoCircleOutlined,
  MessageOutlined,
  PercentageOutlined,
  SearchOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

import { KpiTile } from '../../components/kpi/KpiTile';
import { LiveIndicator } from '../../components/kpi/LiveIndicator';
import { TrendChart, type TrendPoint } from '../../components/charts/TrendChart';
import { DataTable } from '../../components/table/DataTable';
import { CompanyBudgetControl } from './CompanyBudgetControl';
import { CompanyUserDefaultControl } from './CompanyUserDefaultControl';
import {
  formatCurrency,
  formatNumber,
  formatPercent,
  percentChange,
  EM_DASH,
} from '../../utils/format';
import {
  currentPeriod,
  monthRange,
  monthlyTrendRange,
  priorPeriod,
  formatShortDate,
  toDateString,
} from '../../utils/date';
import { PeriodSelector } from './components/PeriodSelector';
import { UserBudgetEditor } from './components/UserBudgetEditor';
import { EntryPointBreakdown } from './components/EntryPointBreakdown';
import { pickEntryPointRows } from './components/entryPointRows';
import {
  useCompanyTotals,
  useCompanyEntryPoints,
  useCompanyTopUsers,
  useLiveCompanyUsage,
  useLiveCompanyRoster,
  useCompanySeries,
  type CompanyUserRow,
  type CompanyTopUserRow,
  type CompanyRosterUserRow,
  type SeriesPoint,
} from './api/evaUsageClient';

const { Title, Text } = Typography;

/**
 * Row shape the Top Users table renders. The current month sources roster /
 * live rows ({@link CompanyUserRow}); past months source the rollup top-users
 * endpoint ({@link CompanyTopUserRow}). The table only reads the fields common
 * to both (name/email/id, totals, lastActiveAt), so a union covers both.
 */
type TopUserDisplayRow = CompanyUserRow | CompanyTopUserRow;

/** Number of users shown in the top-users table. */
const TOP_USER_LIMIT = 5;

/** Page size for the paginated full user list. */
const USER_LIST_PAGE_SIZE = 20;

/**
 * Default effective monthly company spend cap (USD) used as a fallback for the
 * "% of cap" tile when sf-api does not return a company-level effective cap.
 * Default $500 for this wave (override-aware in Wave 5). Configurable via
 * VITE_EVA_COMPANY_MONTHLY_CAP_USD so the default is not a magic constant.
 */
const DEFAULT_COMPANY_MONTHLY_CAP_USD =
  Number(import.meta.env.VITE_EVA_COMPANY_MONTHLY_CAP_USD) || 500;

/** Map series points to the cost line plotted by a trend chart. */
function toCostTrend(series: SeriesPoint[] | undefined): TrendPoint[] {
  return (series ?? []).map((point) => ({
    bucket: point.bucket,
    value: Number.isFinite(Number(point.costUsd)) ? Number(point.costUsd) : 0,
  }));
}

export const EvaCompanyDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { companyId = '' } = useParams<{ companyId: string }>();
  // Company name is passed via router state from the overview's top-companies
  // table (the read endpoint returns only the id); fall back to a generic label.
  const companyName = (location.state as { companyName?: string } | null)?.companyName ?? null;

  // Access control — resolve before issuing any usage request so unauthorized
  // users never trigger a GET (mirrors the overview / companies pattern).
  const { data: canAccess, isLoading: canLoading } = useCan({
    resource: 'eva',
    action: 'show',
  });
  const enabled = canAccess?.can === true && !!companyId;

  // SF-3834: the page can show any month from May 2026 → current month. The
  // selector drives every tile/table/chart. Default to the current month so the
  // page lands on live data.
  //
  // Bugbot (PR #58): do NOT freeze the default with `useState(currentPeriod())`.
  // A tab left open across a UTC month boundary would keep the old month in
  // state, silently turn live off, and show the prior month's rollup until the
  // user re-picked. Instead we store only an EXPLICIT past-month override; while
  // it's null the selected period is derived from `currentPeriod()` every render
  // so it tracks the real current month across the rollover. Selecting the
  // current month clears the override (stays auto-tracking); selecting a past
  // month sets it (intentionally pinned — that's the user's choice).
  const livePeriod = currentPeriod();
  const [periodOverride, setPeriodOverride] = useState<string | null>(null);
  const selectedPeriod =
    periodOverride !== null && periodOverride !== livePeriod ? periodOverride : livePeriod;
  const handlePeriodChange = (period: string) =>
    setPeriodOverride(period === currentPeriod() ? null : period);
  const thisPeriod = selectedPeriod;
  const lastPeriod = priorPeriod(selectedPeriod);
  // Derive the chart from/to on every render (like the period helpers above)
  // rather than memoizing with an empty dep list — otherwise a tab left open
  // across a UTC day or month boundary keeps requesting the original end date
  // and never picks up new buckets until a remount/reload (matches overview).
  // The daily range follows the SELECTED month (clamped to today for the
  // current month, full month for past months); the monthly range stays a
  // rolling 12-month window regardless of selection.
  const dailyRange = monthRange(selectedPeriod);
  const monthlyRange = monthlyTrendRange();

  // The full user list paginates server-side; `page` drives the offset passed
  // to useCompanyTotals, and the DataTable's onChange updates it.
  const [page, setPage] = useState(1);

  // "All Users" email search (SF-3841). `searchInput` is the raw, controlled
  // input value (instant feedback); `rosterSearch` is the debounced term that
  // actually drives the roster query. Debouncing avoids a request per keystroke
  // on the 15s-polling live endpoint. sf-api filters the FULL roster by a
  // case-insensitive email substring before paginating, so a match on any page
  // is found — not just the rows currently loaded.
  const [searchInput, setSearchInput] = useState('');
  const [rosterSearch, setRosterSearch] = useState('');
  useEffect(() => {
    const handle = setTimeout(() => setRosterSearch(searchInput.trim()), 300);
    return () => clearTimeout(handle);
  }, [searchInput]);
  // React Router reuses this view across companies without remounting, so an
  // active email filter would otherwise leak into the next company and drive
  // the roster query with a stale term. Reset DURING render (React's documented
  // "adjust state when a prop changes" pattern) rather than in an effect, so the
  // stale term never reaches the roster query for even one render of the new
  // company. Tracking the previous companyId in state is what makes this safe.
  const [searchCompanyId, setSearchCompanyId] = useState(companyId);
  if (companyId !== searchCompanyId) {
    setSearchCompanyId(companyId);
    setSearchInput('');
    setRosterSearch('');
  }

  // Reset to the first page whenever the company, period, or search term changes
  // — otherwise a user on page 2+ lands on an empty/wrong slice (e.g. drilling
  // into a company with fewer pages, or filtering down to a single match). This
  // MUST run during render, not in a useEffect: the roster query reads `page` in
  // the same render the search term changes, so an effect-based reset would fire
  // the first filtered request with the stale (non-zero) offset and return no
  // rows until a second fetch at page 1 lands. Tracking the previous key in
  // state makes the render-time reset safe (React re-renders before committing).
  const pageResetKey = `${companyId}|${selectedPeriod}|${rosterSearch}`;
  const [lastPageResetKey, setLastPageResetKey] = useState(pageResetKey);
  if (pageResetKey !== lastPageResetKey) {
    setLastPageResetKey(pageResetKey);
    setPage(1);
  }

  const totalsQuery = useCompanyTotals(
    companyId,
    thisPeriod,
    { page, pageSize: USER_LIST_PAGE_SIZE },
    { enabled },
  );
  // LIVE current-month usage (SF-3734 Wave 2): chat-service-proxied counters so
  // the this-month-spend tile and per-user rows tick up in near real time. This
  // page always renders the CURRENT month, so the live source always applies
  // here; we still gate on `isCurrentMonth` to keep the live/rollup switch
  // explicit (and correct should a period selector land later). The live
  // response mirrors the Mongo CompanyTotals shape (same totals + paginated
  // users), so we can swap sources without reshaping. Paginated identically to
  // the rollup so live rows line up with the page the user is on.
  const isCurrentMonth = thisPeriod === currentPeriod();
  const liveQuery = useLiveCompanyUsage(
    companyId,
    { page, pageSize: USER_LIST_PAGE_SIZE },
    { enabled: enabled && isCurrentMonth },
  );
  // LIVE current-month FULL ROSTER (SF-3825): the full active, non-super-admin
  // roster left-joined to live spend, zero-filling absentees and annotating each
  // row with its EFFECTIVE per-user cap. This is the authoritative source for the
  // current-month "All Users" table (every roster member appears, even at $0) and
  // — sliced to the top N — the "Top Users" table. sf-api sorts by live cost desc
  // and paginates, so page 1 already holds the top spenders. The endpoint degrades
  // internally (roster with zeroed spend + a soft `warning`) rather than failing,
  // so the table never empties on a live outage. The current-month data-sourcing
  // rule (epic SF-3725) lives here: current month → live roster; only historical
  // periods would fall back to the Mongo rollup list.
  const rosterQuery = useLiveCompanyRoster(
    companyId,
    { page, pageSize: USER_LIST_PAGE_SIZE },
    { enabled: enabled && isCurrentMonth, search: rosterSearch },
  );
  // Top-N derives from the SAME live roster join, pinned to page 1 / pageSize=N so
  // it always shows the top spenders regardless of which page the All Users table
  // is on. No separate top-users endpoint for the current month (the rollup
  // `useCompanyTopUsers` is intentionally dropped here per SF-3825).
  const topRosterQuery = useLiveCompanyRoster(
    companyId,
    { page: 1, pageSize: TOP_USER_LIMIT },
    { enabled: enabled && isCurrentMonth },
  );
  // PAST-MONTH Top Users (SF-3834): the live roster only covers the current
  // month, so a selected past month sources its top-N from the Mongo rollup
  // top-users endpoint instead. Gated to historical periods only so the current
  // month stays roster-driven (above).
  const topUsersQuery = useCompanyTopUsers(companyId, selectedPeriod, TOP_USER_LIMIT, {
    enabled: enabled && !isCurrentMonth,
  });
  const prevTotalsQuery = useCompanyTotals(
    companyId,
    lastPeriod,
    { page: 1, pageSize: 1 },
    { enabled },
  );
  const dailySeriesQuery = useCompanySeries(
    companyId,
    { ...dailyRange, granularity: 'daily' },
    { enabled },
  );
  const monthlySeriesQuery = useCompanySeries(
    companyId,
    { ...monthlyRange, granularity: 'monthly' },
    { enabled },
  );
  // Spend by AI feature (SF-4567): the saved monthly split, used for past months
  // and as the current month's fallback when the live split is unavailable.
  const entryPointsQuery = useCompanyEntryPoints(companyId, thisPeriod, { enabled });
  // The live split is company-level, so read it from its own live request
  // pinned to page 1 with one user row. Tied to the All Users page, every page
  // change would blank the section and a failed page would swap it to the
  // saved figures, although the feature mix has nothing to do with the list.
  const liveSplitQuery = useLiveCompanyUsage(
    companyId,
    { page: 1, pageSize: 1 },
    { enabled: enabled && isCurrentMonth },
  );

  const totals = totalsQuery.data?.data;
  const prevTotals = prevTotalsQuery.data?.data;
  const dailySeries = dailySeriesQuery.data?.data;
  const monthlySeries = monthlySeriesQuery.data?.data;

  // Live current-month payload (mirrors the Mongo CompanyTotals shape). Treat it
  // as available only on a clean (non-error) response with a usable cost — React
  // Query keeps the last successful `data` even when the latest poll errors, so
  // gating on `isError` keeps the displayed figure and the "Live" badge from
  // ever disagreeing. When live is unavailable we transparently fall back to the
  // Mongo rollup (`totals`) so the page still shows a figure (matches overview).
  const live = liveQuery.data?.data;
  const liveAvailable = isCurrentMonth && !liveQuery.isError && live?.totals?.costUsd != null;

  // Spend by AI feature (SF-4567): same live-vs-saved rule as the tiles. The
  // live company payload must be this month's and carry the split (a
  // chat-service from before SF-4565 sends none, so the saved figures are used).
  const liveSplitPayload = liveSplitQuery.data?.data;
  const liveSplit =
    isCurrentMonth && !liveSplitQuery.isError && liveSplitPayload?.period === thisPeriod
      ? liveSplitPayload?.byEntryPoint
      : null;
  const entryPoints = pickEntryPointRows({
    isCurrentMonth,
    live: liveSplit,
    liveLoading: liveSplitQuery.isLoading,
    liveError: liveSplitQuery.isError || (!liveSplitQuery.isLoading && !liveSplit),
    saved: entryPointsQuery.data?.data,
    savedLoading: entryPointsQuery.isLoading,
    savedError: entryPointsQuery.isError,
  });

  // Headline numbers, all null-guarded. This-month spend prefers the live
  // current-month total; charts + last-month stay on Mongo (see below).
  const cost = (liveAvailable ? live?.totals?.costUsd : totals?.totals?.costUsd) ?? null;
  const prevCost = prevTotals?.totals?.costUsd ?? null;
  const costNumber = cost !== null && Number.isFinite(Number(cost)) ? Number(cost) : null;
  const costDelta = percentChange(
    costNumber,
    prevCost !== null && Number.isFinite(Number(prevCost)) ? Number(prevCost) : null,
  );

  const activeUsers = (liveAvailable ? live?.activeUserCount : totals?.activeUserCount) ?? null;
  // Message count for the new Messages tile (SF-3834), sourced from the same
  // payload driving the spend tiles: live current-month total when available,
  // else the Mongo rollup for the selected period.
  const messageCount =
    (liveAvailable ? live?.totals?.messageCount : totals?.totals?.messageCount) ?? null;

  // The company's effective monthly cap: prefer the data-sourced value from
  // sf-api (override-aware, populated in Wave 5) and fall back to the
  // env-configured default for this wave (acceptance criteria #2). The live
  // payload carries the same cap field, so source it from whichever payload is
  // driving the tiles to keep the denominator consistent with the numerator.
  const effectiveCap = useMemo(() => {
    const capSource = liveAvailable ? live?.effectiveMonthlyCapUsd : totals?.effectiveMonthlyCapUsd;
    const fromData = Number(capSource);
    return Number.isFinite(fromData) && fromData > 0 ? fromData : DEFAULT_COMPANY_MONTHLY_CAP_USD;
  }, [liveAvailable, live?.effectiveMonthlyCapUsd, totals?.effectiveMonthlyCapUsd]);

  // % of the company's effective monthly cap used this month.
  const percentOfCap =
    costNumber !== null && effectiveCap > 0 ? (costNumber / effectiveCap) * 100 : null;

  // Average active-user spend = company total cost / activeUserCount; $0 when
  // there are zero active users (acceptance criteria #3).
  const avgActiveUserSpend = useMemo(() => {
    if (costNumber === null) return null;
    if (activeUsers === null) return null;
    return activeUsers > 0 ? costNumber / activeUsers : 0;
  }, [costNumber, activeUsers]);

  // Daily trend (30d) from the Mongo daily rollups. The rollups only cover
  // CLOSED days (through yesterday) — today's bucket is missing or stale until
  // tonight's job runs. SF-3825: augment the chart with a LIVE today-bar so the
  // current day reflects real-time spend. today-bar = live month-to-date total −
  // Σ(current-month daily-rollup days already present, i.e. through yesterday).
  // Frontend-only: it reuses the live roster/usage poll (no new endpoint) and
  // simply appends/replaces today's point on the existing rollup series.
  const dailyTrend = useMemo(() => {
    const base = toCostTrend(dailySeries?.series);
    // Only augment the live current month, and only when we have a usable live
    // month-to-date figure. Otherwise show the rollup series untouched.
    if (!isCurrentMonth || !liveAvailable) return base;
    const liveMtd = Number(live?.totals?.costUsd);
    if (!Number.isFinite(liveMtd)) return base;

    const today = toDateString(new Date());
    const monthPrefix = today.slice(0, 7); // "YYYY-MM"
    // Sum the current month's CLOSED daily rollups (every bucket in this month
    // strictly before today). Those are the days already baked into the rollup;
    // the remainder of the live MTD is today's spend.
    let closedMonthToDate = 0;
    for (const point of base) {
      if (point.bucket < today && point.bucket.startsWith(monthPrefix)) {
        closedMonthToDate += Number.isFinite(point.value) ? point.value : 0;
      }
    }
    // Live MTD includes today; subtract the closed days to isolate today. Clamp
    // at 0 so rollup/live skew (e.g. a late-arriving rollup day) can't render a
    // negative bar.
    const todayValue = Math.max(0, liveMtd - closedMonthToDate);

    // Replace today's bucket if the rollup already emitted a (stale) one, else
    // append it so the chart's right edge is the live today-bar.
    const idx = base.findIndex((p) => p.bucket === today);
    if (idx >= 0) {
      const next = base.slice();
      next[idx] = { ...next[idx], value: todayValue };
      return next;
    }
    return [...base, { bucket: today, value: todayValue }];
  }, [dailySeries, isCurrentMonth, liveAvailable, live?.totals?.costUsd]);
  const monthlyTrend = useMemo(() => toCostTrend(monthlySeries?.series), [monthlySeries]);

  // The live query is supplementary and degrades to the rollup on its own, so
  // its failure is deliberately excluded from the page-level partial-load
  // warning — a missing live endpoint (e.g. sf-api SF-3731 not yet deployed)
  // must not trigger the warning while the rollup is serving the figures.
  const anyError =
    totalsQuery.isError ||
    prevTotalsQuery.isError ||
    dailySeriesQuery.isError ||
    monthlySeriesQuery.isError;

  // The live roster payload (full active roster joined to live spend). Treat it
  // as available on any clean (non-error) response with a users array — unlike
  // the spend tiles it is meaningful even at $0 (a roster of users who haven't
  // spent yet), so we gate on the response shape, not on a non-zero cost. React
  // Query retains the last good `data` across a failed poll, so gating on
  // `isError` keeps the table and its "Live" badge in agreement.
  const roster = rosterQuery.data?.data;
  const rosterAvailable = isCurrentMonth && !rosterQuery.isError && Array.isArray(roster?.users);
  const topRoster = topRosterQuery.data?.data;
  const topRosterAvailable =
    isCurrentMonth && !topRosterQuery.isError && Array.isArray(topRoster?.users);

  // Per-user rows (All Users): the live FULL ROSTER for the current month — every
  // active, non-super-admin user, zero-activity users included at $0.00 — falling
  // back to the Mongo rollup list only when the roster endpoint is unavailable.
  const rosterRows: CompanyRosterUserRow[] = rosterAvailable ? (roster?.users ?? []) : [];
  const fallbackRows = (liveAvailable ? live?.users : totals?.users) ?? [];
  // A SUCCESSFUL roster response is authoritative even when it is empty (e.g. a
  // company with no active non-super-admin users, or an out-of-range page). Gate
  // on rosterAvailable ONLY — never on rosterRows.length — so an authoritative
  // empty roster renders empty instead of silently falling back to rollup data.
  const usingRoster = rosterAvailable;
  // For the current month the roster is AUTHORITATIVE. While it is still loading
  // we must NOT show the live/rollup spenders fallback: that list omits
  // zero-activity members, so flashing it before the roster lands shows a wrong
  // (short) list. Hold rows empty (→ skeleton via userListLoading) until the
  // roster query has SETTLED; only then fall back if it came back unavailable.
  const rosterPending = isCurrentMonth && rosterQuery.isLoading && !rosterQuery.isError;
  const userRows: CompanyUserRow[] = usingRoster ? rosterRows : rosterPending ? [] : fallbackRows;
  const userTotal = usingRoster
    ? (roster?.pagination?.total ?? roster?.rosterUserCount ?? rosterRows.length)
    : rosterPending
      ? 0
      : ((liveAvailable ? live?.pagination?.total : totals?.pagination?.total) ??
        fallbackRows.length);

  // Top-N rows source priority:
  //  1. PAST month → the Mongo rollup top-users endpoint (the live roster only
  //     covers the current month);
  //  2. dedicated top-roster query (whole-company top-N, server-sorted);
  //  3. MAIN roster available → derive the top-N from a cost-sorted slice of it
  //     IMMEDIATELY (no need to wait for / spin on the dedicated query; it just
  //     refines the same data when it lands). Keeps Top Users consistent with
  //     All Users and avoids an empty-state flash while the top query loads;
  //  4. no roster source yet but one is still loading → empty (skeleton), so we
  //     never flash a wrong/short list before authoritative data arrives;
  //  5. last resort (all roster sources unavailable) → the live/rollup spenders
  //     list, sorted by cost desc as a best-effort global top-N.
  // EVERY path drops zero-spend rows BEFORE slicing to TOP_USER_LIMIT (SF-3834):
  // the Top Users table ranks SPENDERS, so a $0 user must never occupy a slot.
  const hasSpend = (row: { totals?: { costUsd?: string } }) => Number(row.totals?.costUsd) > 0;
  const topRollupUsers = topUsersQuery.data?.data;
  const topRollupRows = (topRollupUsers?.users ?? []).filter(hasSpend).slice(0, TOP_USER_LIMIT);
  const topFromMainRoster = [...rosterRows]
    .filter(hasSpend)
    .sort((a, b) => Number(b.totals?.costUsd ?? 0) - Number(a.totals?.costUsd ?? 0))
    .slice(0, TOP_USER_LIMIT);
  const topFallbackRows = [...fallbackRows]
    .filter(hasSpend)
    .sort((a, b) => Number(b.totals?.costUsd ?? 0) - Number(a.totals?.costUsd ?? 0))
    .slice(0, TOP_USER_LIMIT);
  // The Top Users table only reads fields common to the roster/rollup rows
  // (name, totals, lastActiveAt), so a union row type covers both sources.
  const topUserRows: TopUserDisplayRow[] = !isCurrentMonth
    ? topRollupRows
    : topRosterAvailable
      ? (topRoster?.users ?? []).filter(hasSpend).slice(0, TOP_USER_LIMIT)
      : usingRoster && !rosterSearch
        ? topFromMainRoster
        : topRosterQuery.isLoading || rosterPending || rosterSearch
          ? []
          : topFallbackRows;

  // A soft warning when the live roster degraded to zeroed spend (sf-api could
  // not reach chat-service). The roster still renders; we just flag staleness.
  const rosterWarning = rosterAvailable ? (roster?.warning ?? null) : null;

  // Keep the skeleton until a value actually resolves OR every source that can
  // supply it has settled — never drop it the moment ONE source settles empty.
  const currentMonthSourcesLoading =
    rosterQuery.isLoading || liveQuery.isLoading || totalsQuery.isLoading;
  const tilesLoading = isCurrentMonth
    ? costNumber === null && (liveQuery.isLoading || totalsQuery.isLoading)
    : totalsQuery.isLoading;
  // Once the roster is AUTHORITATIVELY available its rows are final — even an
  // empty array (a company with no active members) is a real answer. Do NOT keep
  // skeletoning for supplementary live/totals fetches in that case, or an empty
  // roster would spin forever instead of showing its empty state. Only fall back
  // to "all current-month sources loading" while the roster itself is unsettled.
  const userListLoading = isCurrentMonth
    ? !usingRoster && userRows.length === 0 && currentMonthSourcesLoading
    : totalsQuery.isLoading;
  // Top Users is final once EITHER roster source is available (the dedicated
  // top-roster query, or the main roster we derive the top-N from). Only spin
  // while a roster source is still loading; ignore supplementary live/totals.
  const topUsersLoading = isCurrentMonth
    ? !topRosterAvailable &&
      !usingRoster &&
      topUserRows.length === 0 &&
      (topRosterQuery.isLoading || currentMonthSourcesLoading)
    : topUsersQuery.isLoading;

  // Refetch every current-month source after a per-user budget edit so the
  // affected row's "% of effective cap" (whose denominator just changed) updates.
  const refetchCurrentMonth = () => {
    rosterQuery.refetch?.();
    topRosterQuery.refetch?.();
    liveQuery.refetch?.();
  };

  const topUserColumns: ColumnsType<TopUserDisplayRow> = [
    {
      title: 'User',
      key: 'user',
      render: (_v, row) => row.userName || row.userEmail || row.userId || EM_DASH,
    },
    {
      title: 'Spend',
      key: 'cost',
      align: 'right',
      render: (_v, row) => formatCurrency(row.totals?.costUsd),
    },
    {
      title: 'Messages',
      key: 'messages',
      align: 'right',
      render: (_v, row) => formatNumber(row.totals?.messageCount ?? null),
    },
    {
      title: 'Last Active',
      key: 'lastActive',
      render: (_v, row) => formatShortDate(row.lastActiveAt),
    },
  ];

  // PAST-MONTH cap columns (SF-3834): historical caps/budgets are not tracked,
  // so the "% of Cap" and "Monthly Budget" cells render an em-dash inside a
  // Tooltip with a small (i) info icon so the explanation is discoverable. The
  // icon is decorative; the Tooltip carries the accessible text.
  const historicalCapCell = (
    <Tooltip title="Historical caps are not tracked; budgets reflect the current month only.">
      <span>
        {EM_DASH}
        <InfoCircleOutlined
          aria-hidden
          style={{ marginInlineStart: 4, fontSize: 12, cursor: 'help', opacity: 0.45 }}
        />
      </span>
    </Tooltip>
  );

  // The All Users table is keyed on the live roster row, which carries the extra
  // roster fields (isActive / effectiveCapUsd / isOverride). When we fall back to
  // the rollup/live spenders list those fields are absent, so each accessor is
  // null-guarded and the budget editor only renders for rows that actually carry
  // a userId (every roster row does).
  const userListColumns: ColumnsType<CompanyRosterUserRow> = [
    {
      // The All Users table is searched by EMAIL (SF-3841), so display the email
      // to match what's being searched. Name/userId remain fallbacks for the rare
      // roster row that carries no email.
      title: 'User',
      key: 'user',
      render: (_v, row) => row.userEmail || row.userName || row.userId || EM_DASH,
    },
    {
      // The roster enumerates every active member; a zero-activity user shows
      // $0.00 here rather than being omitted. Label inactive rows explicitly.
      title: 'Status',
      key: 'status',
      render: (_v, row) =>
        row.isActive === false ? (
          <Tag>Inactive</Tag>
        ) : Number(row.totals?.costUsd) > 0 ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="default">No activity</Tag>
        ),
    },
    {
      title: 'This-Month Cost',
      key: 'cost',
      align: 'right',
      render: (_v, row) => formatCurrency(row.totals?.costUsd),
    },
    {
      title: 'Messages',
      key: 'messages',
      align: 'right',
      render: (_v, row) => formatNumber(row.totals?.messageCount ?? null),
    },
    {
      // Denominator is the user's EFFECTIVE cap (per-user override or default),
      // computed server-side on the roster row — not a flat env cap. Historical
      // months don't track caps, so the cell blanks with a tooltip (SF-3834).
      title: '% of Cap',
      key: 'percentOfCap',
      align: 'right',
      render: (_v, row) =>
        isCurrentMonth ? formatPercent(row.percentOfMonthlyCap) : historicalCapCell,
    },
    {
      // Inline per-user budget editor (SF-3825). Mirrors the company budget
      // control; rows do NOT click through to the admin users table. Stop click
      // propagation so interacting with the editor doesn't trigger the row's
      // deep-link navigation. Budgets reflect the current month only — past
      // months show the blank-with-tooltip cell instead (SF-3834).
      title: 'Monthly Budget',
      key: 'budget',
      render: (_v, row) =>
        !isCurrentMonth ? (
          historicalCapCell
        ) : row.userId ? (
          <span
            onClick={(e) => e.stopPropagation()}
            role="presentation"
            style={{ display: 'inline-block' }}
          >
            <UserBudgetEditor
              userId={row.userId}
              companyId={companyId}
              effectiveCapUsd={row.effectiveCapUsd ?? null}
              isOverride={row.isOverride === true}
              enabled={enabled}
              onSaved={refetchCurrentMonth}
            />
          </span>
        ) : (
          EM_DASH
        ),
    },
    {
      title: 'Last Active',
      key: 'lastActive',
      render: (_v, row) => formatShortDate(row.lastActiveAt),
    },
  ];

  if (canLoading) {
    return <Spin style={{ display: 'flex', justifyContent: 'center', marginTop: 100 }} />;
  }

  if (canAccess?.can !== true) {
    return (
      <Result
        status="403"
        title="Not Authorized"
        subTitle={canAccess?.reason ?? 'You do not have permission to view Eva usage.'}
      />
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <Button
          type="link"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/eva')}
          style={{ paddingLeft: 0 }}
        >
          Back to Eva overview
        </Button>
        <Row justify="space-between" align="middle" wrap gutter={[16, 8]}>
          <Col flex="auto">
            <Title level={3} style={{ marginBottom: 0 }}>
              {companyName || 'Company'} — Eva Usage
            </Title>
            <Text type="secondary">
              {isCurrentMonth ? 'Current month' : 'Historical'} ({selectedPeriod}) usage for this
              company
            </Text>
          </Col>
          <Col>
            <PeriodSelector
              value={selectedPeriod}
              onChange={handlePeriodChange}
              disabled={!enabled}
            />
          </Col>
        </Row>
      </div>

      {anyError && (
        <Alert
          type="warning"
          showIcon
          message="Some usage data could not be loaded"
          description="One or more Eva usage endpoints did not respond. Figures may be incomplete."
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <KpiTile
            title="This-Month Spend"
            value={formatCurrency(cost)}
            icon={<DollarOutlined />}
            loading={tilesLoading}
            deltaPercent={costDelta}
            deltaCaption="vs last month"
            higherIsBetter={false}
            badge={liveAvailable ? <LiveIndicator /> : undefined}
            hint={
              liveAvailable
                ? 'Live current-month spend, updated without waiting for the nightly rollup'
                : undefined
            }
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KpiTile
            title="Last-Month Spend"
            value={formatCurrency(prevCost)}
            icon={<DollarOutlined />}
            loading={prevTotalsQuery.isLoading}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KpiTile
            title="% of Monthly Cap"
            value={formatPercent(percentOfCap)}
            icon={<PercentageOutlined />}
            loading={tilesLoading}
            badge={liveAvailable ? <LiveIndicator /> : undefined}
            hint={`Of the company's ${formatCurrency(effectiveCap)} effective monthly cap`}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KpiTile
            title="Avg Active-User Spend"
            value={formatCurrency(avgActiveUserSpend)}
            icon={<TeamOutlined />}
            loading={tilesLoading}
            badge={liveAvailable ? <LiveIndicator /> : undefined}
            hint="Company total cost ÷ active users this month"
          />
        </Col>
      </Row>

      {/*
        One row, four equal-height tiles (SF-3850). The first two are the Eva
        budget editors — the per-company OVERALL cap (SF-3737) and the
        per-company DEFAULT per-user cap (SF-3850) — and the next two are the
        Active Users and Messages KPIs (SF-3834). The budget editors let an
        authorized operator set/clear the caps that the "% of Monthly Cap" tile
        above is measured against; on save they refetch the company totals so
        that tile reflects the new cap without a manual reload. align="stretch"
        + height:100% on every card keeps all four boxes the same height
        regardless of which one is tallest.
      */}
      <Row gutter={[16, 16]} align="stretch">
        <Col xs={24} sm={12} lg={6}>
          <CompanyBudgetControl
            companyId={companyId}
            enabled={enabled}
            onSaved={() => {
              totalsQuery.refetch?.();
              liveQuery.refetch?.();
            }}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <CompanyUserDefaultControl
            companyId={companyId}
            enabled={enabled}
            onSaved={() => {
              totalsQuery.refetch?.();
              liveQuery.refetch?.();
              // The per-user DEFAULT cap is the denominator for every roster
              // row's "% of Cap" / per-user budget cell (for users without an
              // individual override), so refetch the All Users roster too —
              // otherwise those cells stay on the old default until a manual
              // reload even though the new cap is already persisted.
              rosterQuery.refetch?.();
              topRosterQuery.refetch?.();
            }}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KpiTile
            title="Active Users"
            value={formatNumber(activeUsers)}
            icon={<TeamOutlined />}
            loading={tilesLoading}
            higherIsBetter
            badge={liveAvailable ? <LiveIndicator /> : undefined}
            hint="Users with Eva activity in the selected period"
            style={{ height: '100%' }}
          />
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <KpiTile
            title="Messages"
            value={formatNumber(messageCount)}
            icon={<MessageOutlined />}
            loading={tilesLoading}
            higherIsBetter
            badge={liveAvailable ? <LiveIndicator /> : undefined}
            hint="Total Eva messages in the selected period"
            style={{ height: '100%' }}
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <TrendChart
            title="Monthly Spend (12 months)"
            data={monthlyTrend}
            granularity="monthly"
            loading={monthlySeriesQuery.isLoading}
            valueFormatter={(v) => formatCurrency(v)}
          />
        </Col>
        <Col xs={24} lg={12}>
          <TrendChart
            title={`Daily Spend (${isCurrentMonth ? 'this month to date' : selectedPeriod})`}
            data={dailyTrend}
            granularity="daily"
            loading={dailySeriesQuery.isLoading}
            valueFormatter={(v) => formatCurrency(v)}
          />
        </Col>
      </Row>

      <EntryPointBreakdown
        title="Spend by AI Feature"
        period={thisPeriod}
        rows={entryPoints.rows}
        isLive={entryPoints.isLive}
        loading={entryPoints.loading}
        failed={entryPoints.failed}
      />

      <div>
        <Space style={{ marginBottom: 12 }}>
          <Title level={5} style={{ margin: 0 }}>
            Top {TOP_USER_LIMIT} Users by Spend
          </Title>
          <Tag color="purple">{thisPeriod}</Tag>
          {topRosterAvailable ? (
            <LiveIndicator title="Top users reflect live current-month spend" />
          ) : null}
        </Space>
        <DataTable<TopUserDisplayRow>
          data={topUserRows}
          columns={topUserColumns}
          loading={topUsersLoading}
          rowKey={(row, index) => row.userId ?? `top-user-${index}`}
          pagination={false}
          emptyText="No user activity this period"
        />
      </div>

      <div>
        <Space style={{ marginBottom: 12 }}>
          <Title level={5} style={{ marginBottom: 0, marginTop: 0 }}>
            All Users
          </Title>
          {rosterAvailable || liveAvailable ? (
            <LiveIndicator title="Per-user costs reflect live current-month usage" />
          ) : null}
        </Space>
        {isCurrentMonth ? (
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search by email…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ maxWidth: 320, marginBottom: 12 }}
            aria-label="Search users by email"
          />
        ) : null}
        {rosterWarning ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Live usage temporarily unavailable"
            description={rosterWarning}
          />
        ) : null}
        <DataTable<CompanyRosterUserRow>
          data={userRows as CompanyRosterUserRow[]}
          columns={userListColumns}
          loading={userListLoading}
          rowKey={(row, index) => row.userId ?? `user-${index}`}
          pagination={{
            current: page,
            pageSize: USER_LIST_PAGE_SIZE,
            total: userTotal,
            showSizeChanger: false,
            hideOnSinglePage: false,
            onChange: (nextPage) => setPage(nextPage),
          }}
          emptyText={
            rosterSearch ? `No users match “${rosterSearch}”` : 'No Eva activity for this company'
          }
        />
      </div>
    </Space>
  );
};
